// @intent: Pure layout helpers for playable code, runtime render models, and verifier geometry checks
// @flow: callers pass serializable rects/sizes -> helpers return deterministic coordinates or viewport transforms
// @stale: update if shared viewport transform semantics change or TZ section 3.7 adds layout modes

import type {
  LayoutHelpers,
  PlayableAnchor,
  PlayableFillMode,
  PlayablePoint,
  PlayableRect,
  PlayableSize,
  PlayableViewportTransform,
} from './api';

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

export function center(child: PlayableSize, parent: PlayableRect): PlayablePoint {
  return {
    x: parent.x + (parent.width - child.width) / 2,
    y: parent.y + (parent.height - child.height) / 2,
  };
}

export function alignTo(child: PlayableSize, target: PlayableRect, anchor: PlayableAnchor): PlayablePoint {
  const horizontal = anchor.endsWith('right')
    ? target.x + target.width - child.width
    : anchor.endsWith('left')
      ? target.x
      : target.x + (target.width - child.width) / 2;
  const vertical = anchor.startsWith('bottom')
    ? target.y + target.height - child.height
    : anchor.startsWith('top')
      ? target.y
      : target.y + (target.height - child.height) / 2;
  return { x: horizontal, y: vertical };
}

export function responsiveScale(
  source: PlayableSize,
  target: PlayableSize,
  mode: Exclude<PlayableFillMode, 'stretch'>,
): PlayableViewportTransform {
  const scale = mode === 'cover'
    ? Math.max(target.width / source.width, target.height / source.height)
    : Math.min(target.width / source.width, target.height / source.height);

  return {
    scaleX: scale,
    scaleY: scale,
    offsetX: (target.width - source.width * scale) / 2,
    offsetY: (target.height - source.height * scale) / 2,
  };
}

export function fillParent(child: PlayableSize, parent: PlayableSize, mode: PlayableFillMode): PlayableRect {
  if (mode === 'stretch') {
    return { x: 0, y: 0, width: parent.width, height: parent.height };
  }

  const transform = responsiveScale(child, parent, mode);
  const x = round1(transform.offsetX);
  const y = round1(transform.offsetY);
  return {
    x,
    y,
    width: round1(child.width * transform.scaleX),
    height: round1(child.height * transform.scaleY),
  };
}

export function safeArea(viewport: PlayableSize): {
  top: PlayableRect;
  gameplay: PlayableRect;
  cta: PlayableRect;
} {
  const topHeight = Math.max(72, Math.round(viewport.height * 0.16));
  const ctaHeight = Math.max(76, Math.round(viewport.height * 0.16));

  return {
    top: { x: 0, y: 0, width: viewport.width, height: topHeight },
    gameplay: {
      x: 0,
      y: topHeight,
      width: viewport.width,
      height: Math.max(0, viewport.height - topHeight - ctaHeight),
    },
    cta: {
      x: 0,
      y: viewport.height - ctaHeight,
      width: viewport.width,
      height: ctaHeight,
    },
  };
}

export const layoutHelpers: LayoutHelpers = {
  center,
  alignTo,
  fillParent,
  safeArea,
  responsiveScale,
};
